import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  ArrowLeft, Calendar, DollarSign, Users, TrendingUp, 
  Truck, Star, BarChart2, RefreshCw, ChevronLeft, ChevronRight
} from "lucide-react";

const ReportsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("daily");
  const [loading, setLoading] = useState(true);
  const [dailyReport, setDailyReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [driverPerformance, setDriverPerformance] = useState(null);
  const [topRoutes, setTopRoutes] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [dailyRes, revenueRes, driversRes, routesRes] = await Promise.all([
        api.get(`/reports/daily-summary?date=${selectedDate}`),
        api.get("/reports/revenue"),
        api.get("/reports/driver-performance"),
        api.get("/reports/top-routes")
      ]);
      setDailyReport(dailyRes.data);
      setRevenueReport(revenueRes.data);
      setDriverPerformance(driversRes.data);
      setTopRoutes(routesRes.data);
    } catch (error) {
      toast.error("Failed to load reports");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Reports & Analytics</h1>
              <p className="text-sm text-muted-foreground">Business insights and performance metrics</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchReports} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: "daily", label: "Daily Summary", icon: Calendar },
            { id: "revenue", label: "Revenue", icon: DollarSign },
            { id: "drivers", label: "Driver Performance", icon: Truck },
            { id: "routes", label: "Top Routes", icon: TrendingUp }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Daily Summary Tab */}
        {activeTab === "daily" && dailyReport && (
          <div className="space-y-6">
            {/* Date Selector */}
            <Card className="dashboard-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-center gap-4">
                  <Button variant="ghost" size="icon" onClick={() => changeDate(-1)} data-testid="prev-date">
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-auto"
                      data-testid="date-picker"
                    />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => changeDate(1)} data-testid="next-date">
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="dashboard-card" data-testid="total-sales-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-green-500/10 flex items-center justify-center">
                      <DollarSign className="h-7 w-7 text-green-500" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-green-500">{formatCurrency(dailyReport.total_sales)}</p>
                      <p className="text-sm text-muted-foreground">Total Sales</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-card" data-testid="driver-pay-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center">
                      <Truck className="h-7 w-7 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-blue-500">{formatCurrency(dailyReport.driver_pay)}</p>
                      <p className="text-sm text-muted-foreground">Driver Pay ({dailyReport.driver_pay_percentage}%)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-card" data-testid="total-bookings-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center">
                      <Calendar className="h-7 w-7 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-purple-500">{dailyReport.total_pickups_today}</p>
                      <p className="text-sm text-muted-foreground">Bookings Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Company Revenue */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Revenue Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-black/5">
                    <p className="text-sm text-muted-foreground">Total Sales</p>
                    <p className="text-xl font-bold">{formatCurrency(dailyReport.total_sales)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-black/5">
                    <p className="text-sm text-muted-foreground">Driver Pay</p>
                    <p className="text-xl font-bold">{formatCurrency(dailyReport.driver_pay)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-black/5">
                    <p className="text-sm text-muted-foreground">Company Revenue</p>
                    <p className="text-xl font-bold text-green-500">{formatCurrency(dailyReport.company_revenue)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-black/5">
                    <p className="text-sm text-muted-foreground">Bookings Created</p>
                    <p className="text-xl font-bold">{dailyReport.total_bookings_created}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bookings by Status */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart2 className="h-5 w-5 text-primary" />
                  Bookings by Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(dailyReport.bookings_by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/5">
                      <span className="capitalize">{status.replace("_", " ")}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Bookings by Vehicle */}
            {dailyReport.bookings_by_vehicle?.length > 0 && (
              <Card className="dashboard-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-primary" />
                    Bookings by Vehicle Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Vehicle Type</th>
                          <th>Bookings</th>
                          <th>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyReport.bookings_by_vehicle.map((v, i) => (
                          <tr key={i}>
                            <td className="capitalize font-medium">{v.vehicle}</td>
                            <td>{v.count}</td>
                            <td className="text-green-500">{formatCurrency(v.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === "revenue" && revenueReport && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="dashboard-card">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-500">{formatCurrency(revenueReport.summary.total_revenue)}</p>
                </CardContent>
              </Card>
              <Card className="dashboard-card">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Paid Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(revenueReport.summary.total_paid_revenue)}</p>
                </CardContent>
              </Card>
              <Card className="dashboard-card">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Driver Pay</p>
                  <p className="text-2xl font-bold text-blue-500">{formatCurrency(revenueReport.summary.driver_pay)}</p>
                </CardContent>
              </Card>
              <Card className="dashboard-card">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Total Bookings</p>
                  <p className="text-2xl font-bold">{revenueReport.summary.total_bookings}</p>
                </CardContent>
              </Card>
            </div>

            {/* Daily Data Table */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle>Daily Revenue (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="data-table">
                    <thead className="sticky top-0 bg-card">
                      <tr>
                        <th>Date</th>
                        <th>Bookings</th>
                        <th>Completed</th>
                        <th>Revenue</th>
                        <th>Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueReport.daily_data.slice().reverse().map((day, i) => (
                        <tr key={i}>
                          <td className="font-medium">{day.date}</td>
                          <td>{day.bookings}</td>
                          <td>{day.completed}</td>
                          <td>{formatCurrency(day.revenue)}</td>
                          <td className="text-green-500">{formatCurrency(day.paid_revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Driver Performance Tab */}
        {activeTab === "drivers" && driverPerformance && (
          <div className="space-y-6">
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-primary" />
                  Driver Performance (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Driver</th>
                        <th>Status</th>
                        <th>Total Trips</th>
                        <th>Completed</th>
                        <th>Completion %</th>
                        <th>Revenue</th>
                        <th>Driver Pay</th>
                        <th>Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driverPerformance.drivers.map((driver, i) => (
                        <tr key={i}>
                          <td>
                            <div>
                              <p className="font-medium">{driver.driver_name}</p>
                              <p className="text-xs text-muted-foreground">{driver.driver_email}</p>
                            </div>
                          </td>
                          <td>
                            <Badge variant={driver.is_active ? "default" : "secondary"}>
                              {driver.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td>{driver.total_trips}</td>
                          <td>{driver.completed_trips}</td>
                          <td>
                            <span className={driver.completion_rate >= 90 ? "text-green-500" : driver.completion_rate >= 70 ? "text-yellow-500" : "text-red-500"}>
                              {driver.completion_rate}%
                            </span>
                          </td>
                          <td>{formatCurrency(driver.total_revenue)}</td>
                          <td className="text-blue-500">{formatCurrency(driver.driver_pay)}</td>
                          <td>
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                              <span>{driver.avg_rating > 0 ? driver.avg_rating : "-"}</span>
                              {driver.total_ratings > 0 && (
                                <span className="text-xs text-muted-foreground">({driver.total_ratings})</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top Routes Tab */}
        {activeTab === "routes" && topRoutes && (
          <div className="space-y-6">
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Most Popular Routes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Pickup</th>
                        <th>Dropoff</th>
                        <th>Bookings</th>
                        <th>Total Revenue</th>
                        <th>Avg Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRoutes.top_routes.map((route, i) => (
                        <tr key={i}>
                          <td className="font-bold text-primary">{i + 1}</td>
                          <td className="max-w-[200px]">
                            <p className="truncate">{route.pickup}</p>
                          </td>
                          <td className="max-w-[200px]">
                            <p className="truncate">{route.dropoff}</p>
                          </td>
                          <td className="font-medium">{route.booking_count}</td>
                          <td className="text-green-500">{formatCurrency(route.total_revenue)}</td>
                          <td>{formatCurrency(route.avg_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReportsPage;
